"use client";

import { useCallback, useEffect, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { workspacesApi, type Workspace } from "@/lib/api";
import {
    hasClientAuthToken,
    reconcileClientAuthSession,
    subscribeAuthSession,
} from "@/lib/authSession";
import { navigateToSignin } from "@/lib/openLineExternalBrowser";
import { captureFinancialWorkIntentFromLocation } from "@/lib/financialWork/intent";

function unwrapWorkspace(data: unknown): Workspace | null {
    if (!data || typeof data !== "object") return null;
    const maybe = data as { data?: unknown; workspace?: unknown };
    const inner = maybe.data ?? maybe.workspace ?? data;
    if (!inner || typeof inner !== "object") return null;
    return inner as Workspace;
}

function normalizePath(pathname: string): string {
    return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

function isEmbeddedInServiceIframe(): boolean {
    try {
        if (typeof window === "undefined") return false;
        const inIframe = window.self !== window.top;
        if (!inIframe) return false;
        return new URL(window.location.href).searchParams.get("embedded") === "true";
    } catch {
        return false;
    }
}

function hasFinancialWorkHarnessNoAuth(): boolean {
    try {
        if (typeof window === "undefined") return false;
        return new URL(window.location.href).searchParams.get("fwHarnessNoAuth") === "1";
    } catch {
        return false;
    }
}

function isPublicPathForRequest(
    normalizedPath: string,
    hasWorkspaceContext: boolean,
): boolean {
    const publicExactPaths = [
        "/",
        "/login",
        "/signin",
        "/signin/external",
        "/login/external",
        "/login/aera",
        "/login/aera/external",
        "/oauth/close",
        "/integrations",
        "/privacy",
        "/terms-of-use",
        "/support",
        "/favicon.ico",
    ];

    const publicPathPrefixes = [
        "/auth/",
        "/api/",
        "/_next",
        "/login",
        "/signin",
        "/oauth/",
        "/integrations",
        "/service/",
        // `/artifacts/table` ตัวเองต้องล็อกอิน — ยกเว้นหน้า summary-external
        // ที่มี auth bridge ผ่าน hash param เอง
        "/artifacts/table/summary-external",
        "/support/",
        "/go/",
    ];

    const isPublicPath =
        publicExactPaths.includes(normalizedPath) ||
        publicPathPrefixes.some((path) => normalizedPath.startsWith(path));

    // หน้า `/` (home) เป็น public โดยปกติ (landing/marketplace) แต่เมื่อเปิด
    // ในบริบทของ workspace (มี ?workspaceId=...) ถือเป็นพื้นที่ที่ต้องล็อกอิน
    // เช่น `/?source=order&workspaceId=...&serviceId=...`
    if (isPublicPath && normalizedPath === "/" && hasWorkspaceContext) {
        return false;
    }

    return isPublicPath;
}

export default function AuthWrapper({
    children,
}: {
    children: React.ReactNode;
}) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const hasWorkspaceContext = searchParams?.has("workspaceId") ?? false;
    const [isAuthorized, setIsAuthorized] = useState(false);

    const runAuthCheck = useCallback(() => {
        if (!pathname) return;

        if (isEmbeddedInServiceIframe()) {
            setIsAuthorized(true);
            return;
        }
        if (hasFinancialWorkHarnessNoAuth()) {
            setIsAuthorized(true);
            return;
        }

        const normalizedPath = normalizePath(pathname);

        if (isPublicPathForRequest(normalizedPath, hasWorkspaceContext)) {
            setIsAuthorized(true);
            return;
        }

        if (reconcileClientAuthSession()) {
            window.dispatchEvent(new Event("auth:logout"));
        }

        if (hasClientAuthToken()) {
            setIsAuthorized(true);
            return;
        }

        setIsAuthorized(false);

        const currentPath =
            typeof window !== "undefined"
                ? `${window.location.pathname}${window.location.search}${window.location.hash}`
                : pathname || "/";
        // LIFF-00 backup if callbackUrl is dropped during OAuth/binding.
        captureFinancialWorkIntentFromLocation();
        const loginUrl = `/signin?callbackUrl=${encodeURIComponent(currentPath)}`;
        navigateToSignin(router, loginUrl, { replace: true });
    }, [pathname, router, hasWorkspaceContext]);

    useEffect(() => {
        runAuthCheck();
    }, [runAuthCheck]);

    useEffect(() => subscribeAuthSession(runAuthCheck), [runAuthCheck]);

    // Workspace ownership guard: เมื่อ URL มี ?workspaceId=... สำหรับเส้นทาง
    // ที่ผูกกับ workspace context ให้ตรวจว่า user คือผู้สร้าง (ownerId) ของ
    // workspace นั้นจริง — ถ้าไม่ใช่ให้เด้งกลับหน้าหลัก เพื่อกัน user เข้าถึง
    // workspace ที่ไม่ได้เป็นเจ้าของ
    useEffect(() => {
        if (!pathname) return;
        if (typeof window === "undefined") return;
        if (isEmbeddedInServiceIframe()) return;

        const normalizedPath = normalizePath(pathname);

        // เฉพาะหน้าที่ใช้ workspaceId เป็นบริบทผู้ใช้งาน (ผู้สร้าง workspace)
        // หน้า /artifacts/table/summary-external ใช้ auth bridge ของตัวเอง
        // (สำหรับ external/LIFF) จึงข้าม
        const needsCheck =
            normalizedPath === "/" ||
            (normalizedPath.startsWith("/artifacts/table") &&
                !normalizedPath.startsWith("/artifacts/table/summary-external"));
        if (!needsCheck) return;

        let workspaceId: string | null = null;
        try {
            workspaceId = (
                new URL(window.location.href).searchParams.get("workspaceId") ?? ""
            ).trim();
        } catch {
            return;
        }
        if (!workspaceId) return;

        let userId: string | null = null;
        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) return; // basic auth check จะจัดการ redirect /signin เอง
            const user = JSON.parse(userStr) as Record<string, unknown> | null;
            const rawId = user?.id ?? user?.userId ?? null;
            userId = typeof rawId === "string" && rawId.trim() ? rawId.trim() : null;
        } catch {
            return;
        }
        if (!userId) return;

        let cancelled = false;
        (async () => {
            const bounceHome = () => {
                if (cancelled) return;
                router.replace("/");
            };
            try {
                const raw = await workspacesApi.getById(workspaceId as string);
                if (cancelled) return;
                const ws = unwrapWorkspace(raw);
                const ownerId = ws?.ownerId?.trim() || null;
                // Missing owner or a failed lookup is not proof the user
                // doesn't own this shop — bouncing to `/` then lands on the
                // last hired workspace and undoes an explicit switch.
                if (ownerId && ownerId !== userId) {
                    bounceHome();
                }
            } catch {
                // Network / hairpin failures must not reset the workspace.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [pathname, router, searchParams]);

    if (
        !isAuthorized &&
        pathname &&
        !isPublicPathForRequest(normalizePath(pathname), hasWorkspaceContext)
    ) {
        return null;
    }

    return <>{children}</>;
}
